import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listQuotes, deleteQuote, updateQuoteQuantity, formatEuro } from "@/lib/api";
import { structureLabel } from "@/lib/utils";
import { toast } from "sonner";
import { Loader2, FileText, Trash2, ArrowRight, Plus, Minus, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function Quotes() {
  const [quotes, setQuotes] = useState(null);
  const [updatingQtyId, setUpdatingQtyId] = useState(null);

  const load = () => {
    listQuotes()
      .then(setQuotes)
      .catch((e) => toast.error("Erreur", { description: e.message }));
  };
  useEffect(load, []);

  const handleDelete = async (id) => {
    try {
      await deleteQuote(id);
      toast.success("Devis supprimé");
      load();
    } catch (e) {
      toast.error("Impossible de supprimer");
    }
  };

  const handleQuantityChange = async (quote, nextQuantity) => {
    if (nextQuantity < 1 || nextQuantity > 100 || quote.status === "commande") return;
    setUpdatingQtyId(quote.id);
    // Mise à jour optimiste pour un retour immédiat dans le tableau.
    setQuotes((prev) => prev.map((q) => (q.id === quote.id ? { ...q, config: { ...q.config, quantity: nextQuantity } } : q)));
    try {
      const updated = await updateQuoteQuantity(quote.id, nextQuantity);
      setQuotes((prev) => prev.map((q) => (q.id === quote.id ? updated : q)));
    } catch (e) {
      toast.error("Impossible de mettre à jour la quantité");
      load();
    } finally {
      setUpdatingQtyId(null);
    }
  };

  if (!quotes) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <main data-testid="quotes-page" className="w-full px-4 sm:px-6 lg:px-10 2xl:px-16 py-8 lg:py-12">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="eyebrow mb-2">Historique</p>
          <h1 className="text-3xl sm:text-4xl">Mes devis</h1>
        </div>
        <Link to="/configurateur?new=1" data-testid="new-quote-cta" className="btn-primary">
          <Plus className="w-4 h-4" /> Nouveau devis
        </Link>
      </div>

      {quotes.length === 0 ? (
        <div className="paper p-16 text-center">
          <FileText className="w-8 h-8 mx-auto text-muted-foreground mb-4" />
          <h2 className="font-heading text-2xl mb-2">Aucun devis pour l'instant</h2>
          <p className="text-muted-foreground text-sm mb-6">Créez votre premier devis depuis le configurateur.</p>
          <Link to="/configurateur" className="btn-primary">Ouvrir le configurateur</Link>
        </div>
      ) : (
        <div className="paper max-w-full overflow-x-auto" data-testid="quotes-table">
          <table className="w-full min-w-[820px]">
            <thead className="border-b border-border bg-background/50">
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="p-4">Référence</th>
                <th className="p-4">Client</th>
                <th className="p-4">Modèle</th>
                <th className="p-4">Date</th>
                <th className="p-4 text-center">Qté</th>
                <th className="p-4 text-right">Total HT</th>
                <th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <tr key={q.id} data-testid={`quote-row-${q.id}`} className="border-b border-border last:border-0 hover:bg-background/40">
                  <td className="p-4 font-mono text-xs">{q.reference}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-1.5">
                      <div className="font-medium text-sm">{q.client.contact_name}</div>
                      {q.client.notes && (
                        <MessageSquare
                          data-testid={`quote-notes-indicator-${q.id}`}
                          className="w-3.5 h-3.5 text-primary shrink-0"
                          title={q.client.notes}
                        />
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">{q.client.company || q.client.email}</div>
                  </td>
                  <td className="p-4 text-sm">
                    <div>{structureLabel(q.template_name, (q.config.sections || []).some((s) => s.has_door))}</div>
                    <div className="text-xs text-muted-foreground">{q.config.total_width}×{q.config.height}×{q.config.depth} · {q.exterior_finish_name}</div>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    {new Date(q.created_at).toLocaleDateString("fr-FR")}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        data-testid={`quote-quantity-decr-${q.id}`}
                        onClick={() => handleQuantityChange(q, (q.config.quantity || 1) - 1)}
                        disabled={updatingQtyId === q.id || q.status === "commande" || (q.config.quantity || 1) <= 1}
                        className="w-6 h-6 rounded-full border border-border flex items-center justify-center hover:bg-background disabled:opacity-40"
                      ><Minus className="w-3 h-3" /></button>
                      <span data-testid={`quote-quantity-value-${q.id}`} className="w-6 text-center tabular-nums text-sm">
                        {updatingQtyId === q.id ? <Loader2 className="w-3 h-3 animate-spin mx-auto" /> : (q.config.quantity || 1)}
                      </span>
                      <button
                        data-testid={`quote-quantity-incr-${q.id}`}
                        onClick={() => handleQuantityChange(q, (q.config.quantity || 1) + 1)}
                        disabled={updatingQtyId === q.id || q.status === "commande"}
                        className="w-6 h-6 rounded-full border border-border flex items-center justify-center hover:bg-background disabled:opacity-40"
                      ><Plus className="w-3 h-3" /></button>
                    </div>
                  </td>
                  <td className="p-4 text-right tabular-nums font-medium">{formatEuro(q.total_ht_all_items || q.breakdown.total_ht || q.breakdown.total_ttc)}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-1 justify-end">
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(q.id)} data-testid={`delete-${q.id}`}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                      <Link to={`/devis/${q.id}`} data-testid={`view-${q.id}`} className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-card">
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
